import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DatePicker } from 'antd';
import { bookingsApi } from '../../../api/adminApi';
import './BookingList.css';

const { RangePicker } = DatePicker;
const DEFAULT_LIMIT = 10;

const TABS = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
  { key: 'all', label: 'All' },
];

function BookingList() {
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [activeTab, setActiveTab] = useState('upcoming');
  const [dateRange, setDateRange] = useState(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const isFiltered = dateRange && dateRange[0] && dateRange[1];

  const loadBookings = (pageNum = page, range = dateRange, tab = activeTab) => {
    setLoading(true);
    setError('');
    const start = range?.[0] ? range[0].format('YYYY-MM-DD') : null;
    const end = range?.[1] ? range[1].format('YYYY-MM-DD') : null;
    bookingsApi
      .list(pageNum, limit, start, end, tab)
      .then((res) => {
        setBookings(res.list);
        setTotal(res.total);
        setPage(res.page);
      })
      .catch((err) => setError(err.userMessage || 'Failed to load bookings'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadBookings(1, null, 'upcoming');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setDateRange(null);
    loadBookings(1, null, tab);
  };

  const handleRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
      loadBookings(1, dates, activeTab);
    } else {
      setDateRange(null);
      loadBookings(1, null, activeTab);
    }
  };

  const bookingName = (b) => b.parent_name ?? b.name ?? b.full_name ?? '—';
  const bookingEmail = (b) => b.email ?? b.parent_email ?? '—';
  const bookingPhone = (b) => b.phone_number ?? b.phone ?? '—';
  const bookingChild = (b) => {
    const name = b.child_name ?? '—';
    const age = b.child_age != null ? ` (${b.child_age})` : '';
    return name !== '—' ? `${name}${age}` : '—';
  };
  const bookingDate = (b) => {
    const str =
      b.slot_start ?? b.appointment_date ?? b.date ?? b.scheduled_at ?? b.created_at ?? b.createdAt ?? '';
    if (!str) return '—';
    try {
      const d = new Date(str);
      return isNaN(d.getTime()) ? str : d.toLocaleDateString(undefined, { dateStyle: 'short' });
    } catch {
      return str;
    }
  };
  const bookingTime = (b) => {
    const start = b.slot_start ?? b.appointment_time ?? b.time ?? b.slot;
    const end = b.slot_end;
    if (!start) return '—';
    try {
      const dStart = new Date(start);
      if (isNaN(dStart.getTime())) return start;
      const timeStart = dStart.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
      if (!end) return timeStart;
      const dEnd = new Date(end);
      if (isNaN(dEnd.getTime())) return timeStart;
      const timeEnd = dEnd.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
      return `${timeStart} – ${timeEnd}`;
    } catch {
      return start;
    }
  };
  const bookingStatus = (b) => b.status ?? '—';
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="admin-bookings">
      <div className="admin-bookings-header">
        <h2>Booking Appointments</h2>
        <div className="date-range-picker">
          <RangePicker
            key={`dateRange-${activeTab}-${isDatePickerOpen}`}
            onChange={handleRangeChange}
            separator="-"
            value={dateRange}
            allowClear={true}
            placeholder={['Start date', 'End date']}
            disabledDate={(current) => {
              if (dateRange && dateRange[0]) {
                const maxEndDate = dateRange[0].add(90, 'day');
                if (current && current > maxEndDate) {
                  return true;
                }
              }
              return false;
            }}
            style={{
              width: '260px',
              border: '1px solid #D9D9D9',
              borderRadius: '8px',
              padding: '7px 12px',
              height: '44px',
            }}
            format="MMM DD, YYYY"
            open={isDatePickerOpen}
            onOpenChange={(open) => setIsDatePickerOpen(open)}
          />
        </div>
      </div>

      <div className="admin-bookings-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`admin-bookings-tab${activeTab === tab.key ? ' admin-bookings-tab--active' : ''}`}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="admin-bookings-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="admin-bookings-loading">Loading bookings…</div>
      ) : (
        <div className="admin-bookings-table-wrap">
          <table className="admin-bookings-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Child</th>
                <th>Date</th>
                <th>Time</th>
                <th>Status</th>
                <th className="admin-bookings-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 ? (
                <tr>
                  <td colSpan={8} className="admin-bookings-empty">
                    No bookings found{isFiltered ? ' for the selected date range' : ''}.
                  </td>
                </tr>
              ) : (
                bookings.map((b) => (
                  <tr key={b.id}>
                    <td>{bookingName(b)}</td>
                    <td>{bookingEmail(b)}</td>
                    <td>{bookingPhone(b)}</td>
                    <td>{bookingChild(b)}</td>
                    <td>{bookingDate(b)}</td>
                    <td>{bookingTime(b)}</td>
                    <td>{bookingStatus(b)}</td>
                    <td className="admin-bookings-actions">
                      <Link
                        to={`/admin/bookings/${b.id}`}
                        className="admin-bookings-btn admin-bookings-btn-view"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > 0 && totalPages > 1 && (
        <div className="admin-bookings-pagination">
          <button
            type="button"
            className="admin-bookings-page-btn"
            onClick={() => loadBookings(page - 1)}
            disabled={page <= 1}
          >
            Previous
          </button>
          <span className="admin-bookings-page-info">
            Page {page} of {totalPages} ({total} total)
          </span>
          <button
            type="button"
            className="admin-bookings-page-btn"
            onClick={() => loadBookings(page + 1)}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default BookingList;
